import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Badge,
  DocumentTextIcon,
  EyeIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../../components/ui';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { moduleConfig } from './config';
import {
  useCrewQuestionnaires,
  useQuestionnaireKinds,
  type CrewQuestionnaire,
} from './useData';
import {
  ReferenceSidebarCollapseButton,
  ReferenceSidebarSplitter,
} from '../reference-workspaces/ReferenceSidebarChrome';
import { useResizablePanel } from '../reference-workspaces/useResizablePanel';

const List: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const confirm = useConfirm();
  const { can } = useAuth();
  const permissionKey = moduleConfig.module.permissionModuleKey ?? 'crew_parameters';
  const canDelete = can(permissionKey, 'delete');
  const canEdit = can(permissionKey, 'edit');

  const {
    splitRef,
    panelWidth,
    collapsed,
    setCollapsed,
    panelResizing,
    startResize,
    minWidth,
    maxWidth,
  } = useResizablePanel({
    storageKey: 'ref-questionnaires-split',
    defaultWidth: 320,
    minWidth: 220,
    maxWidth: 480,
  });

  const kindIdFromUrl = searchParams.get('kindId');
  const initialKindId =
    kindIdFromUrl && Number.isFinite(Number(kindIdFromUrl)) ? Number(kindIdFromUrl) : null;

  const [selectedKindId, setSelectedKindId] = useState<number | null>(initialKindId);
  const [kindFilter, setKindFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();

  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'sortOrder', direction: 'asc' },
    {
      defaultDirectionForKey: (key) => (key === 'createdAt' || key === 'updatedAt' ? 'desc' : 'asc'),
    },
  );

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { data: kindsRaw, isLoading: kindsLoading } = useQuestionnaireKinds();

  const kinds = useMemo(
    () =>
      kindsRaw
        .slice()
        .sort(
          (a, b) =>
            (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name),
        ),
    [kindsRaw],
  );

  const filteredKinds = useMemo(() => {
    const q = kindFilter.trim().toLowerCase();
    if (!q) return kinds;
    return kinds.filter(
      (k) =>
        k.name.toLowerCase().includes(q) ||
        (k.code?.trim().toLowerCase().includes(q) ?? false),
    );
  }, [kinds, kindFilter]);

  useEffect(() => {
    if (kinds.length === 0) {
      if (selectedKindId != null) setSelectedKindId(null);
      return;
    }
    const valid = selectedKindId != null && kinds.some((k) => k.id === selectedKindId);
    if (!valid) {
      setSelectedKindId(kinds[0].id);
    }
  }, [kinds, selectedKindId]);

  const activeKindId = useMemo(() => {
    if (selectedKindId != null && kinds.some((k) => k.id === selectedKindId)) {
      return selectedKindId;
    }
    return kinds[0]?.id ?? null;
  }, [kinds, selectedKindId]);

  const selectedKind = useMemo(
    () => (activeKindId != null ? kinds.find((k) => k.id === activeKindId) ?? null : null),
    [kinds, activeKindId],
  );

  const questionnairesEnabled = activeKindId != null;

  const { data, total, isLoading, mutate } = useCrewQuestionnaires(
    questionnairesEnabled ? search || undefined : undefined,
    page,
    itemsPerPage,
    questionnairesEnabled
      ? {
          kindId: activeKindId,
          sortKey: sortConfig.key,
          sortDirection: sortConfig.direction,
        }
      : null,
  );

  useEffect(() => {
    setPage(1);
  }, [search, activeKindId, itemsPerPage, sortConfig.key, sortConfig.direction]);

  useEffect(() => {
    const current = searchParams.get('kindId');
    const nextValue = activeKindId != null ? String(activeKindId) : null;
    if (current === nextValue || (current == null && nextValue == null)) return;
    const next = new URLSearchParams(searchParams);
    if (nextValue != null) {
      next.set('kindId', nextValue);
    } else {
      next.delete('kindId');
    }
    setSearchParams(next, { replace: true });
  }, [activeKindId, searchParams, setSearchParams]);

  const totalPages = Math.max(1, Math.ceil((total ?? 0) / itemsPerPage));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const formatDate = moduleConfig.utils.formatDate;

  const newQuestionnaireHref =
    activeKindId != null
      ? `${moduleConfig.module.route.new}?kindId=${activeKindId}`
      : moduleConfig.module.route.new;

  const handleDelete = async (e: React.MouseEvent, row: CrewQuestionnaire) => {
    e.stopPropagation();
    const ok = await confirm({
      title: moduleConfig.delete.title,
      message: `Delete “${row.name}”?`,
      confirmText: moduleConfig.delete.confirmText,
      cancelText: moduleConfig.delete.cancelText,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await moduleConfig.delete.handler(row.id);
      await mutate();
      showSuccess('Questionnaire deleted.');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? moduleConfig.delete.errorMessage;
      showError(message);
    }
  };

  const clearKindFilter = () => setKindFilter('');
  const clearQuestionnaireSearch = () => {
    setSearchInput('');
    setSearch('');
  };

  return (
    <div
      ref={splitRef}
      className={cn('flex h-full min-h-0 gap-0 p-1', panelResizing && 'select-none')}
    >
      {collapsed ? (
        <aside
          className={cn(
            'flex w-11 shrink-0 flex-col items-center rounded-2xl border py-2',
            colors.border,
            colors.surface,
          )}
        >
          <ReferenceSidebarCollapseButton collapsed onToggle={() => setCollapsed(false)} />
          <span
            className={cn(
              'mt-2 text-[10px] font-semibold uppercase tracking-wider [writing-mode:vertical-rl] rotate-180',
              colors.textMuted,
            )}
          >
            Kinds
          </span>
        </aside>
      ) : (
        <div className="shrink-0 min-h-0" style={{ width: panelWidth }}>
          <GeneralList
            className="h-full"
            title="Questionnaire kinds"
            description={`${kinds.length} total`}
            actions={
              <div className="flex items-center gap-2">
                {canEdit ? (
                  <Link
                    to="/parameters/crew/questionnaire-kinds/new"
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium text-white',
                      colors.primary,
                    )}
                  >
                    <PlusIcon className="h-3.5 w-3.5" />
                    New
                  </Link>
                ) : null}
                <ReferenceSidebarCollapseButton collapsed={false} onToggle={() => setCollapsed(true)} />
              </div>
            }
            density="compact"
            search={{
              value: kindFilter,
              onChange: setKindFilter,
              placeholder: 'Filter kinds…',
            }}
            hasFilters={Boolean(kindFilter.trim())}
            onClearFilters={clearKindFilter}
            isLoading={kindsLoading}
            loadingLabel="Loading kinds…"
            isEmpty={!kindsLoading && filteredKinds.length === 0}
            emptyState={{
              icon: <DocumentTextIcon className="h-10 w-10" />,
              title: kindFilter.trim() ? 'No kinds match' : 'No kinds',
              hint: kindFilter.trim()
                ? 'Try a different filter.'
                : 'Create a questionnaire kind first.',
              action: kindFilter.trim() ? (
                <button
                  type="button"
                  onClick={clearKindFilter}
                  className={cn(
                    'inline-flex items-center rounded-xl border px-4 py-2.5 text-sm font-medium',
                    colors.border,
                    colors.text,
                  )}
                >
                  Clear filter
                </button>
              ) : canEdit ? (
                <Link
                  to="/parameters/crew/questionnaire-kinds/new"
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
                    colors.primary,
                  )}
                >
                  <PlusIcon className="h-4 w-4" />
                  New kind
                </Link>
              ) : undefined,
            }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
                  <th className={cn('sticky top-0 z-10 font-medium', colors.surface)}>Kind</th>
                  <th className={cn('sticky top-0 z-10 w-20 font-medium', colors.surface)}>Code</th>
                </tr>
              </thead>
              <tbody>
                {filteredKinds.map((kind) => {
                  const active = activeKindId === kind.id;
                  return (
                    <tr
                      key={kind.id}
                      onClick={() => setSelectedKindId(kind.id)}
                      className={cn(
                        'cursor-pointer border-b transition-colors last:border-b-0',
                        colors.border,
                        active
                          ? cn(colors.primary, colors.primaryText)
                          : cn(colors.text, colors.interactiveHover),
                      )}
                    >
                      <td className="font-medium">{kind.name}</td>
                      <td className={cn('tabular-nums font-mono text-xs', active ? 'text-white/80' : colors.textMuted)}>
                        {kind.code || ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </GeneralList>
        </div>
      )}

      {!collapsed && (
        <ReferenceSidebarSplitter
          panelWidth={panelWidth}
          minWidth={minWidth}
          maxWidth={maxWidth}
          resizing={panelResizing}
          onMouseDown={startResize}
          label="Resize kinds panel"
        />
      )}

      <div className="relative min-h-0 min-w-0 flex-1">
        {collapsed && (
          <div className="absolute left-2 top-2 z-10">
            <ReferenceSidebarCollapseButton collapsed onToggle={() => setCollapsed(false)} />
          </div>
        )}
        <GeneralList
          className="h-full"
          title={selectedKind?.name ?? 'Questionnaires'}
          description={
            selectedKind
              ? `${isLoading ? '…' : total ?? 0} questionnaire${total === 1 ? '' : 's'}${
                  selectedKind.code ? ` · ${selectedKind.code}` : ''
                }`
              : 'Select a kind'
          }
          search={{
            value: searchInput,
            onChange: setSearchInput,
            placeholder: moduleConfig.search.placeholder,
          }}
          actions={
            canEdit && questionnairesEnabled ? (
              <Link
                to={newQuestionnaireHref}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
                  colors.primary,
                )}
              >
                <PlusIcon className="h-4 w-4" />
                New questionnaire
              </Link>
            ) : null
          }
          hasFilters={Boolean(search.trim())}
          onClearFilters={clearQuestionnaireSearch}
          sorting={{ sortConfig, onSort: handleSort }}
          isLoading={questionnairesEnabled && isLoading}
          loadingLabel="Loading questionnaires…"
          isEmpty={!isLoading && (!questionnairesEnabled || data.length === 0)}
          emptyState={{
            icon: <DocumentTextIcon className="h-12 w-12" />,
            title: !questionnairesEnabled
              ? 'Select a kind'
              : search.trim()
                ? moduleConfig.emptyStates.noSearchResults
                : 'No questionnaires yet',
            hint: !questionnairesEnabled
              ? 'Choose a questionnaire kind on the left to view its questionnaires.'
              : search.trim()
                ? 'Try adjusting the search.'
                : moduleConfig.emptyStates.noData,
            action: !questionnairesEnabled ? undefined : search.trim() ? (
              <button
                type="button"
                onClick={clearQuestionnaireSearch}
                className={cn(
                  'inline-flex items-center rounded-xl border px-4 py-2.5 text-sm font-medium',
                  colors.border,
                  colors.text,
                )}
              >
                Clear search
              </button>
            ) : canEdit ? (
              <Link
                to={newQuestionnaireHref}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
                  colors.primary,
                )}
              >
                <PlusIcon className="h-4 w-4" />
                New questionnaire
              </Link>
            ) : undefined,
          }}
          pagination={
            questionnairesEnabled
              ? {
                  page,
                  totalPages,
                  total: total ?? 0,
                  itemsPerPage,
                  pageSizeOptions,
                  onPageChange: setPage,
                  onItemsPerPageChange: (size) => {
                    setItemsPerPage(size);
                    setPage(1);
                  },
                }
              : undefined
          }
        >
          <table className="w-full text-sm">
            <thead>
              <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
                <th className={cn('sticky top-0 z-10 w-px whitespace-nowrap font-medium', colors.surface)} />
                <GeneralListSortableTh sortKey="name">Name</GeneralListSortableTh>
                <th className={cn('sticky top-0 z-10 font-medium', colors.surface)}>Questions</th>
                <GeneralListSortableTh sortKey="sortOrder">Order</GeneralListSortableTh>
                <GeneralListSortableTh sortKey="isActive">Active</GeneralListSortableTh>
                <GeneralListSortableTh sortKey="createdAt">Created</GeneralListSortableTh>
                <th className={cn('sticky top-0 z-10 w-full font-medium', colors.surface)} aria-hidden />
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => navigate(moduleConfig.module.route.detail(row.id))}
                  className={cn(
                    'cursor-pointer border-b transition-colors last:border-b-0',
                    colors.border,
                    colors.text,
                    colors.interactiveHover,
                  )}
                >
                  <td className="w-px whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-0.5">
                      <Link
                        to={moduleConfig.module.route.detail(row.id)}
                        className={cn(
                          'inline-flex h-8 w-8 items-center justify-center rounded-lg',
                          colors.textMuted,
                          colors.interactiveHover,
                        )}
                        title="View"
                        aria-label="View"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Link>
                      {canEdit ? (
                        <Link
                          to={moduleConfig.module.route.edit(row.id)}
                          className={cn(
                            'inline-flex h-8 w-8 items-center justify-center rounded-lg',
                            colors.textMuted,
                            colors.interactiveHover,
                          )}
                          title="Edit"
                          aria-label="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Link>
                      ) : null}
                      {canDelete ? (
                        <button
                          type="button"
                          onClick={(e) => handleDelete(e, row)}
                          className={cn(
                            'inline-flex h-8 w-8 items-center justify-center rounded-lg',
                            colors.textMuted,
                            'hover:text-red-600',
                          )}
                          title="Delete"
                          aria-label="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      ) : null}
                    </div>
                  </td>
                  <td className="pr-6 font-medium whitespace-nowrap">{row.name}</td>
                  <td className={cn('pr-6 whitespace-nowrap tabular-nums', colors.textSecondary)}>
                    {row.questions?.length ?? 0}
                  </td>
                  <td className={cn('pr-6 whitespace-nowrap tabular-nums', colors.textSecondary)}>
                    {row.sortOrder}
                  </td>
                  <td className="pr-6 whitespace-nowrap">
                    <Badge variant={row.isActive ? 'success' : 'danger'}>
                      {row.isActive ? 'Yes' : 'No'}
                    </Badge>
                  </td>
                  <td className={cn('pr-6 whitespace-nowrap', colors.textSecondary)}>
                    {formatDate(row.createdAt)}
                  </td>
                  <td className="w-full" aria-hidden />
                </tr>
              ))}
            </tbody>
          </table>
        </GeneralList>
      </div>
    </div>
  );
};

export default List;
